import { useFlags } from "launchdarkly-react-client-sdk";
import { Control, Controller, useWatch } from "react-hook-form";

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import useTheme from "@mui/material/styles/useTheme";

import {
  EVENT_TYPES,
  EVENT_TYPE_CLINIC_EVENT,
  EVENT_TYPE_PERSONAL_EVENT,
  EVENT_TYPE_PERSONAL_WORKSHIFT
} from "constants/appointments";
import {
  CLINIC_EVENTS_FEATURE,
  HIDE_PERSONAL_AND_CLINIC_EVENT_OPTION_FOR_USERS,
  SCHEDULE_VIEW_IN_PATIENT_HEADER,
  VIEW_WORK_SHIFT_IN_SCHEDULER
} from "constants/feature-flags";
import { GROUP_PATIENT_CARE_COORDINATOR } from "constants/staff";
import usePatientCreateAppointmentMode from "features/scheduling/patient-create-appointment-mode-modal/use-patient-create-appointment-mode.hook";
import useUserRoles from "hooks/use-user-roles.hook";

import { AppointmentFormValues } from "../appointment-form.component";

type EventTypeFieldProps = {
  control: Control<AppointmentFormValues, object>;
  sx?: SxProps;
};

export default function EventTypeField({ control, sx }: EventTypeFieldProps) {
  const theme = useTheme();
  const flags = useFlags();
  const isClinicEventsEnabled = flags[CLINIC_EVENTS_FEATURE];
  const viewWorkShiftInSchedule = flags[VIEW_WORK_SHIFT_IN_SCHEDULER];
  const isScheduleViewInPatientHeaderFeature =
    flags[SCHEDULE_VIEW_IN_PATIENT_HEADER];
  const hidePersonalAndClinicEventOptionForUsers =
    flags[HIDE_PERSONAL_AND_CLINIC_EVENT_OPTION_FOR_USERS];
  const { patientCreateAppointmentMode } = usePatientCreateAppointmentMode();
  const [eventId, eventType] = useWatch({
    control,
    name: ["id", "event_type"]
  });
  const { isAdminOrBusinessOwner, hasRole } = useUserRoles();

  const showClinicEventType =
    (isClinicEventsEnabled &&
      (isAdminOrBusinessOwner ||
        hasRole(GROUP_PATIENT_CARE_COORDINATOR))) ||
    (Boolean(eventId) && eventType === EVENT_TYPE_CLINIC_EVENT);

  // When editing an existing Personal or Clinic block, allow switching between the two.
  // All other chip types (Appointment, Work Shift) remain locked on edit.
  const isEditingBlockEvent =
    Boolean(eventId) &&
    (eventType === EVENT_TYPE_PERSONAL_EVENT || eventType === EVENT_TYPE_CLINIC_EVENT);

  const excludeEventTypes: any[] = [];
  if (!viewWorkShiftInSchedule) {
    excludeEventTypes.push(EVENT_TYPE_PERSONAL_WORKSHIFT);
  }
  if (!showClinicEventType) {
    excludeEventTypes.push(EVENT_TYPE_CLINIC_EVENT);
  }
  if (hidePersonalAndClinicEventOptionForUsers) {
    excludeEventTypes.push(EVENT_TYPE_PERSONAL_EVENT);
    excludeEventTypes.push(EVENT_TYPE_CLINIC_EVENT);
  }
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      sx={sx}
    >
      <Controller
        name="event_type"
        control={control}
        render={({ field }) => (
          <Stack direction="row" spacing={1}>
            {EVENT_TYPES.getOptions(excludeEventTypes).map((option) => (
              <Chip
                key={option.label}
                label={option.label}
                color="primary"
                variant={
                  field.value !== option.value ? "outlined" : undefined
                }
                disabled={
                  (isScheduleViewInPatientHeaderFeature &&
                    patientCreateAppointmentMode) ||
                  (isEditingBlockEvent
                    ? option.value !== EVENT_TYPE_PERSONAL_EVENT &&
                      option.value !== EVENT_TYPE_CLINIC_EVENT
                    : Boolean(eventId))
                }
                onClick={() => {
                  field.onChange(option.value);
                  field.onBlur();
                }}
                sx={{
                  "&:hover, &.MuiChip-outlined:hover": {
                    color: "#fff",
                    backgroundColor: theme.palette.primary.dark
                  }
                }}
              />
            ))}
          </Stack>
        )}
      />
    </Box>
  );
}
